// PR #168 review (2026-07-30) — scheme gate for a feed-sourced URL that is used
// as a NAVIGATION TARGET.
//
// `ProductSummary.imageUrl` comes from a feed and nothing validates its scheme
// on the way through the catalogue, the projection or `browse.client`.
//
// Rendering it as an `<img src>` is fine. Putting it in an `<a href>` is not: a
// `javascript:` or `data:` value in a supplier feed becomes a click-to-execute
// link. React 19 only WARNS about a `javascript:` href in development — it still
// renders it in a production build, so the warning is absent exactly when it
// would matter.
//
// The PDP hero's zoom affordance (`ProductHero`) is the only place in `apps/web`
// that navigates to a feed value. It falls through to its existing disabled "no
// larger image available" branch when the URL does not pass, so a hostile or
// malformed value presents identically to a product with no image at all —
// never a live link, never a dead enabled control.

/**
 * The URL if it parses as an absolute `http:`/`https:` URL, else `null`.
 *
 * Everything else is rejected: any other scheme (`javascript:`, `data:`,
 * `vbscript:`, `file:`, …), a relative or protocol-relative path, and any string
 * `new URL` cannot parse. An allowlist, not a denylist — the set of schemes a
 * browser will execute is not one we can enumerate, but the two that are valid
 * for a CDN image URL are (`imageUrl` is documented as an absolute CDN URL).
 */
export function safeImageHref(url: string | null | undefined): string | null {
  const trimmed = url?.trim() ?? '';
  if (trimmed === '') return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    // Not an absolute URL (relative path, whitespace-mangled, garbage).
    return null;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? trimmed : null;
}
